<script setup lang="ts">
import { onBeforeMount, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { GenericArray } from '@Types';
import type { PaginateResult } from '@Providers';
import type { ProductAtCart } from '../../../types';
import { API_URL } from '@Constants';
import { useSessionStore } from '@Stores';
import { useADialog } from '@Atoms';
import { ROLES } from '@AuthModule';
import { useActiveFilter, useFilters } from '@FilterModule';
import { useCartStore } from '@CartModule';
import { useCleanSharedRefs, useDataTable, useDataTableFilters } from '@DataTableModule';
import { useShoppingCartStore } from '@ShoppingCartModule';
import { GetProductListService } from '../../../services';
import { useProductsStore } from '../../../stores';
import productsAndServicesTableHelper from './productsAndServicesTableHelper';

defineExpose({ onGetProductListService, clearQuantityForProduct });

const props = withDefaults(
  defineProps<{
    cartActionLoading?: boolean;
    hasHeader?: boolean;
    isProductsAndServicesView?: boolean;
    loadingButton?: boolean;
    total?: any;
  }>(),
  {
    isProductsAndServicesView: false,
  }
);

const emits = defineEmits<{
  onAddToCart: [ProductAtCart];
  onMinusFromCart: [ProductAtCart, number];
  onUpdateCart: [];
}>();

const { can } = useSessionStore();
const { stats, totalItems } = storeToRefs(useProductsStore());

// TODO :: TASK-2103 This can be removed after all clients migrate to the new cart. Leave only shoppingCart then
const cartStore = useSessionStore().shelterProfile?.enable_shopping_cart ? useShoppingCartStore() : useCartStore();
const { cartData } = storeToRefs(cartStore);

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { activeFilters } = useActiveFilter();
const { cleanDataTableSharedRefs } = useCleanSharedRefs();
const { clearFilters, getActiveFilters } = useFilters();
const { columns, frozenFields, rows, rowsPerPage, totalRecords } = useDataTable();
const { initialColumns, activeColumns, mainRequestUrlParams, searchQuery, selectFilter } = useDataTableFilters();
const { t } = useI18n();

const isEmptyTable = ref<boolean | undefined>(false);
const loading = ref(false);
const quantity = ref<GenericArray>({});
const refreshQuantityColumn = ref(false);

watch(searchQuery, () => refreshData());

onBeforeMount(() => cleanDataTableSharedRefs());
onMounted(onGetProductListService);

// HELPERS
function getCartQuantityForProduct(product: ProductAtCart): number {
  const items = product.type === 'product' ? cartData.value?.products : cartData.value?.services;
  const cartItem = items?.find((item: ProductAtCart) => item._id === product._id);

  return cartItem?.quantity ?? 0;
}

function isMinusDisabledForProduct(product: ProductAtCart): boolean {
  return getCartQuantityForProduct(product) === 0;
}

function clearQuantityForProduct(productId: string) {
  quantity.value[productId] = null;
}

function displayQuantity(product: ProductAtCart): number {
  return props.isProductsAndServicesView ? quantity.value[product._id] ?? 0 : getCartQuantityForProduct(product);
}

function onPlus(product: ProductAtCart) {
  if (!props.isProductsAndServicesView) {
    emits('onAddToCart', { ...product, quantity: getCartQuantityForProduct(product) + 1 });
    return;
  }

  const next = (quantity.value[product._id] ?? 0) + 1;
  quantity.value[product._id] = next;

  emits('onAddToCart', { ...product, quantity: next });
}

function onMinus(product: ProductAtCart) {
  if (props.isProductsAndServicesView) {
    const current = quantity.value[product._id] ?? 0;
    const next = Math.max(0, current - 1);
    quantity.value[product._id] = next;
    emits('onAddToCart', { ...product, quantity: next });
    return;
  }

  onRemoveProductRequest(product, 1);
}

// EVENTS
async function onGetProductListService(isParentRefresh: boolean = false) {
  selectFilter.value = '';
  quantity.value = {};
  loading.value = !isParentRefresh;
  refreshQuantityColumn.value = isParentRefresh;

  await GetProductListService(rowsPerPage.value, mainRequestUrlParams.value)
    .then((result) => {
      _handler(result);
      _setColumns();

      if (result?.filters?.fields) _setFilters(result.filters.fields);
    })
    .finally(() => {
      loading.value = false;
      refreshQuantityColumn.value = false;
    });

  function _handler({ data, total, empty_table }: PaginateResult) {
    isEmptyTable.value = empty_table;
    rows.value = data;
    totalRecords.value = total;
    totalItems.value = total;
    stats.value = [
      {
        name: t('stats.products'),
        value: data.filter((item: any) => item.type === 'product').length.toString(),
      },
      {
        name: t('stats.services'),
        value: data.filter((item: any) => item.type === 'service').length.toString(),
      },
      {
        name: t('stats.total'),
        value: total.toString(),
      },
    ];
  }

  function _setColumns() {
    const staticColumns = [
      {
        name: 'sku',
        label: 'SKU#',
      },
      {
        name: 'category',
        label: 'Category',
      },
      {
        name: 'product',
        label: 'Name',
      },
      {
        name: 'type',
        label: 'Type',
      },
      {
        name: 'price',
        label: 'Price',
      },
    ];

    const dynamicColumns = staticColumns.map((field: any) => productsAndServicesTableHelper(field));

    columns.value = dynamicColumns;
    initialColumns.value = dynamicColumns.map(({ field }) => field);
    activeColumns.value = initialColumns.value;

    frozenFields.value = ['sku'];
  }

  function _setFilters(fields: any) {
    activeFilters.value = getActiveFilters(fields);
  }
}

function onRemoveProductRequest(product: ProductAtCart, quantityToRemove: number) {
  const currentQuantity = getCartQuantityForProduct(product);
  const notLastProduct = currentQuantity - quantityToRemove > 0;

  if (notLastProduct) {
    return emits('onMinusFromCart', product, quantityToRemove);
  }

  if (props.isProductsAndServicesView) return;

  activeModal.value = SYSTEM_MODALS.CART_REMOVE_PRODUCT_CONFIRM;
  activeModalProps.value = {
    productName: product.product ?? '',
    onConfirm: () => emits('onMinusFromCart', product, currentQuantity),
  };
  dialogVisibility.value = true;
}

async function refreshData() {
  loading.value = true;

  await onGetProductListService().finally(() => (loading.value = false));
}
</script>

<template>
  <header>
    <MHead
      v-if="hasHeader"
      compact
      :loading
      :stats
      :title="$t('productsAndServices.title')"
      :subtitle="$t('productsAndServices.description')"
    />

    <FiltersTemplate
      :loading
      :dialog="{ title: $t('animals.filters.title.inCare') }"
      :hasDataTableFilters="!!hasHeader"
      @onClearFilters="clearFilters() && refreshData()"
      @onApplyFilters="refreshData"
    >
      <template #slotRight>
        <Button
          v-if="totalItems > 0 && hasHeader"
          class="af-button af-button-success"
          icon="pi pi-cart-plus"
          :disabled="total === 0"
          :label="`${$t('cart.label.addItems')} (${total || 0})`"
          :loading="loadingButton"
          @click="emits('onUpdateCart')"
        />
      </template>
    </FiltersTemplate>
  </header>

  <MDataTable
    v-if="!isEmptyTable"
    :loading
    :class="{ 'mb-8': !hasHeader }"
    :hasAsideColumn="false"
    :hasSelector="false"
    :tunnelProps="{ header: 'Quantity' }"
    @onParamsUpdate="refreshData"
  >
    <template #tunnel="{ props }">
      <div class="flex items-center justify-center">
        <AProductQuantityStepper
          v-if="!refreshQuantityColumn"
          :quantity="displayQuantity(props)"
          :compact="!hasHeader"
          :disabledMinus="
            !!cartActionLoading ||
            (isProductsAndServicesView ? displayQuantity(props) === 0 : isMinusDisabledForProduct(props))
          "
          :disabledPlus="!!cartActionLoading"
          @onPlus="onPlus(props)"
          @onMinus="onMinus(props)"
        />

        <AProductQuantityStepper
          v-else
          :quantity="displayQuantity(props)"
          :compact="!hasHeader"
          disabledMinus
          disabledPlus
        />
      </div>
    </template>
  </MDataTable>

  <MEmptySection
    v-else-if="isEmptyTable && !loading"
    class="mt-4 h-[62vh]"
    icon="pi pi-box"
    label="productsAndServices.emptyState.label"
  >
    <template #customAction>
      <a
        v-if="can(ROLES.OUTCOME_SETTINGS)"
        :href="`${API_URL}/shelter/new-ui/settings/products/manage-products`"
      >
        <Button
          class="af-button af-button-success h-9"
          icon="pi pi-plus"
          :label="$t('productsAndServices.emptyState.action')"
        />
      </a>
    </template>
  </MEmptySection>
</template>
